import React from 'react';
import {
  BadgeLabelList,
  MAX_VISIBLE_BADGES_DESKTOP,
} from '@/components/orders/badge-label-list';
import {
  BORDER,
  focusedItem,
  SCREEN_PADDING_LARGE,
  TYPOGRAPHY,
} from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import {
  DefaultOrderableItemListItemActions,
  OrderableItemListItemActions,
} from '@/components/orders/orderable-item-list-item-actions';
import {
  DISPLAY_SIZE_TO_IMAGE_SIZE,
  OrderableItemListItemImage,
} from '@/components/orders/orderable-item-list-item-image';
import {
  getVisibleQuantity,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemLabels } from '@/components/orders/orderable-item-labels';
import { OrderableItemLabelType } from '@/enums';
import { OrderGuideInfo } from '@/components/orders/order-guide-info';
import { PAID_SEARCH_BORDER_WIDTH } from '@/components/ads/paid-search-item-header';
import { RenderActionsFn } from '@/components/orders/item-detail-actions';
import { SampleRequest } from '@/components/ads/sample-request';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import {
  useIsPhotoHidden,
  useItemDisplayOptions,
} from '@/contexts/new-order-context';
import { VariantPackOrderInfo } from '@/components/orders/variant-pack-order-info';

interface Props {
  action?: React.ReactNode;
  availabilityMap?: Record<string, number>;
  canDecrementToZero?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  disableTextInput?: boolean;
  hideSampleRequest?: boolean;
  inDraggableSheet?: boolean;
  isFocused: boolean;
  isInboundOrder?: boolean;
  isPaidSearchItem?: boolean;
  listDisplaySize: ListDisplaySize;
  onOptionsPress?: (option: ItemListOption) => void;
  option: ItemListOption;
  quantityState?: QuantityStateType;
  renderActions?: RenderActionsFn;
  renderNote?: (skuMetadata: ItemListOptionMetadata) => React.ReactNode;
  renderOrderGuideButtonAction?: (variantPackUUID: string) => React.ReactNode;
  renderSalesRepContent: (
    skuMetadata: ItemListOptionMetadata,
  ) => React.ReactNode;
  showSingleOption?: boolean;
  showTooltipOnMount?: boolean;
  showVariantPackOrderInfo?: boolean;
  source: string;
  updateQuantity?: UpdateQuantityFn;
  visibleLabels?: OrderableItemLabelType[];
}

export const ListViewOrderableItem: React.FC<Props> = ({
  action,
  availabilityMap,
  canDecrementToZero,
  containerStyle,
  disabled,
  disableTextInput,
  hideSampleRequest = false,
  inDraggableSheet,
  isFocused,
  isInboundOrder = false,
  isPaidSearchItem = false,
  listDisplaySize,
  onOptionsPress,
  option,
  quantityState,
  renderActions,
  renderNote,
  renderOrderGuideButtonAction,
  renderSalesRepContent,
  showSingleOption = false,
  showTooltipOnMount = false,
  showVariantPackOrderInfo = false,
  source,
  updateQuantity,
  visibleLabels,
}) => {
  const [styles] = useStyles();
  const { hidePlaceholderImages } = useItemDisplayOptions();

  const metadata = option.metadata!;
  const isPhotoHidden = useIsPhotoHidden(metadata, listDisplaySize);
  const photoSizeStyle = {
    height: DISPLAY_SIZE_TO_IMAGE_SIZE[listDisplaySize],
    width: DISPLAY_SIZE_TO_IMAGE_SIZE[listDisplaySize],
  };

  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  return (
    <View
      style={[
        styles.baseListView,
        isInboundOrder ? styles.minimalBaseListView : null,
        isPaidSearchItem ? styles.paidSearchList : null,
        containerStyle,
        isFocused ? focusedItem() : null,
      ]}
      testID="orderable-item-list-item"
    >
      <OrderableItemLabels
        containerStyle={styles.listRowLabels}
        option={option}
        visibleLabels={visibleLabels}
      />
      <View
        style={styles.mainContentList}
        testID={`vp:${option.metadata?.variantPackUUID}`}
      >
        <View style={styles.listRowContainer}>
          {!isPhotoHidden ? (
            <View style={[styles.photoContainer, photoSizeStyle]}>
              <OrderableItemListItemImage
                alt={altText}
                hidePlaceholderImages={hidePlaceholderImages}
                imageURLs={metadata.photoURLs}
                listDisplaySize={listDisplaySize}
              />
            </View>
          ) : null}
          <View
            style={[
              styles.listRowContent,
              isInboundOrder ? styles.minimalListRowContent : null,
            ]}
          >
            <View style={styles.titleListViewContainer}>
              <Text numberOfLines={2} style={TYPOGRAPHY.pSemiBold}>
                {option.title}
              </Text>
              <Text
                numberOfLines={3}
                style={[TYPOGRAPHY.description, styles.descriptionText]}
              >
                {option.metadata?.description}
              </Text>
              <BadgeLabelList
                badges={metadata.badges}
                iconOnly
                maxVisible={MAX_VISIBLE_BADGES_DESKTOP}
              />
              {!hideSampleRequest && (
                <SampleRequest
                  option={option}
                  source={source}
                  style={styles.sampleRequestButtonGrid}
                />
              )}
            </View>
            <View
              style={
                isInboundOrder
                  ? styles.minimalListRowRight
                  : styles.listRowRight
              }
            >
              {showSingleOption ? (
                <View style={styles.listRowActions}>
                  <View style={BORDER.primary}>
                    {renderNote ? renderNote(metadata) : null}
                    <OrderableItemListItemActions
                      canDecrementToZero={canDecrementToZero}
                      disabled={disabled}
                      disableTextInput={disableTextInput}
                      inDraggableSheet={inDraggableSheet}
                      isFocused={isFocused}
                      onPress={() => onOptionsPress?.(option)}
                      quantityInStock={
                        availabilityMap?.[metadata.variantPackUUID]
                      }
                      quantityState={quantityState}
                      renderActions={renderActions}
                      renderOrderGuideButton={renderOrderGuideButtonAction}
                      showListView
                      showTooltipOnMount={showTooltipOnMount}
                      skus={metadata.skus}
                      updateQuantity={updateQuantity}
                    />
                    {renderSalesRepContent(metadata)}
                  </View>
                </View>
              ) : (
                metadata.skus.map((skuMetadata, index) => {
                  const nonMinimalSkuHeader = showVariantPackOrderInfo ? (
                    <VariantPackOrderInfo variantPackMetadata={skuMetadata} />
                  ) : (
                    <OrderGuideInfo
                      isLastOrderedMessageVisible
                      isParLevelsVisible
                      orderGuidePartitionUUID={
                        skuMetadata.orderGuidePartitionUUID
                      }
                      variantPackUUID={skuMetadata.variantPackUUID}
                    />
                  );
                  return (
                    <View
                      key={`orderable-item-list-item-${
                        skuMetadata.variantPackUUID
                      }-${skuMetadata.paidSearch ? 'paid-search' : 'base'}`}
                      style={[
                        index === 0 ? null : styles.multiSkuWrapper,
                        isInboundOrder ? styles.minimalSkuWrapper : null,
                      ]}
                    >
                      {isInboundOrder ? null : nonMinimalSkuHeader}
                      <View style={BORDER.primary}>
                        {renderNote ? renderNote(skuMetadata) : null}
                        <DefaultOrderableItemListItemActions
                          canDecrementToZero={canDecrementToZero}
                          disabled={disabled}
                          disableTextInput={disableTextInput}
                          inDraggableSheet={inDraggableSheet}
                          isFocused={index === 0 && isFocused}
                          metadata={skuMetadata}
                          quantity={
                            quantityState &&
                            getVisibleQuantity(
                              quantityState,
                              skuMetadata.variantPackUUID,
                            )
                          }
                          renderActions={renderActions}
                          renderOrderGuideButton={renderOrderGuideButtonAction}
                          showListView
                          showTooltipOnMount={showTooltipOnMount}
                          updateQuantity={updateQuantity}
                        />
                        {renderSalesRepContent(skuMetadata)}
                      </View>
                      {isInboundOrder && showVariantPackOrderInfo ? (
                        <VariantPackOrderInfo
                          variantPackMetadata={skuMetadata}
                        />
                      ) : null}
                    </View>
                  );
                })
              )}
            </View>
          </View>
          {action ? (
            <View style={styles.listRowExternalActionContainer}>{action}</View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const BASE_LIST_VIEW_MARGIN = 24;

const useStyles = createStyles((theme) => ({
  baseListView: {
    flex: 1,
    marginHorizontal: SCREEN_PADDING_LARGE,
    marginVertical: BASE_LIST_VIEW_MARGIN,
    paddingHorizontal: PAID_SEARCH_BORDER_WIDTH,
    paddingVertical: 0,
  },
  descriptionText: {
    lineHeight: 20,
    marginTop: 8,
    maxHeight: 60,
  },
  listRowActions: {
    marginBottom: 16,
  },
  listRowContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  listRowContent: {
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'row',
    marginTop: 8,
  },
  listRowExternalActionContainer: {
    marginLeft: 24,
    marginRight: 12,
  },
  listRowLabels: {
    marginBottom: 0,
    marginHorizontal: 8,
  },
  listRowRight: {
    flex: 3,
  },
  mainContentList: {
    backgroundColor: theme.colors.white,
    borderRadius: 4,
    paddingHorizontal: 8,
  },
  minimalBaseListView: {
    marginBottom: BASE_LIST_VIEW_MARGIN,
    marginTop: 12,
  },
  minimalListRowContent: {
    marginTop: 0,
  },
  minimalListRowRight: {
    flex: 1,
  },
  minimalSkuWrapper: {
    gap: 4,
  },
  multiSkuWrapper: {
    marginTop: 8,
  },
  paidSearchList: {
    borderColor: theme.colors.accent,
    borderWidth: 4,
    paddingVertical: 16,
  },
  photoContainer: {
    ...BORDER.primary,
    marginRight: 16,
    overflow: 'hidden',
  },
  sampleRequestButtonGrid: {
    marginTop: 16,
  },
  titleListViewContainer: {
    flex: 1,
    marginRight: 32,
  },
}));
